import { useEffect, useLayoutEffect } from 'react';

let activeLockCount = 0;
let storedOverflow = '';
let storedPaddingRight = '';
let storedPosition = '';
let storedTop = '';
let storedLeft = '';
let storedRight = '';
let storedWidth = '';
let storedHtmlOverflow = '';
let storedHtmlOverscrollBehavior = '';
let lockedScrollY = 0;
let usedIOSFixedPositionLock = false;

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function isIOSDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  return /iP(ad|hone|od)/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

export function _resetScrollLockState() {
  activeLockCount = 0;
  storedOverflow = '';
  storedPaddingRight = '';
  storedPosition = '';
  storedTop = '';
  storedLeft = '';
  storedRight = '';
  storedWidth = '';
  storedHtmlOverflow = '';
  storedHtmlOverscrollBehavior = '';
  lockedScrollY = 0;
  usedIOSFixedPositionLock = false;
}

/**
 * Prevents background scrolling when enabled (e.g., for modals and overlays).
 *
 * Supports ref-counted nesting so multiple consumers can lock simultaneously
 * without conflicting. On iOS, uses a fixed-position workaround to prevent
 * the rubber-band scroll-through behavior, restoring scroll position on unlock.
 *
 * Sets a `--scrollbar-gutter` CSS custom property on `<html>` so siblings
 * (like fixed headers) can compensate for the removed scrollbar width.
 */
export function useScrollLock(enabled: boolean): void {
  useIsomorphicLayoutEffect(() => {
    if (!enabled) return;

    const previousCount = activeLockCount;
    activeLockCount++;
    if (previousCount === 0) {
      const bodyStyle = document.body.style;
      const htmlStyle = document.documentElement.style;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      storedOverflow = bodyStyle.overflow || '';
      storedPaddingRight = bodyStyle.paddingRight || '';
      storedPosition = bodyStyle.position || '';
      storedTop = bodyStyle.top || '';
      storedLeft = bodyStyle.left || '';
      storedRight = bodyStyle.right || '';
      storedWidth = bodyStyle.width || '';
      storedHtmlOverflow = htmlStyle.overflow || '';
      storedHtmlOverscrollBehavior = htmlStyle.overscrollBehavior || '';
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      usedIOSFixedPositionLock = isIOSDevice();

      bodyStyle.overflow = 'hidden';
      htmlStyle.overflow = 'hidden';
      htmlStyle.overscrollBehavior = 'none';
      if (scrollbarWidth > 0) {
        bodyStyle.paddingRight = `${scrollbarWidth}px`;
        document.documentElement.style.setProperty('--scrollbar-gutter', `${scrollbarWidth}px`);
      }

      if (usedIOSFixedPositionLock) {
        bodyStyle.position = 'fixed';
        bodyStyle.top = `-${lockedScrollY}px`;
        bodyStyle.left = '0';
        bodyStyle.right = '0';
        bodyStyle.width = '100%';
      }
    }

    return () => {
      activeLockCount = Math.max(0, activeLockCount - 1);
      if (activeLockCount === 0) {
        const bodyStyle = document.body.style;
        const htmlStyle = document.documentElement.style;

        if (storedOverflow) {
          bodyStyle.overflow = storedOverflow;
        } else {
          bodyStyle.removeProperty('overflow');
        }
        if (storedHtmlOverflow) {
          htmlStyle.overflow = storedHtmlOverflow;
        } else {
          htmlStyle.removeProperty('overflow');
        }
        if (storedHtmlOverscrollBehavior) {
          htmlStyle.overscrollBehavior = storedHtmlOverscrollBehavior;
        } else {
          htmlStyle.removeProperty('overscroll-behavior');
        }
        if (storedPaddingRight) {
          bodyStyle.paddingRight = storedPaddingRight;
        } else {
          bodyStyle.removeProperty('padding-right');
        }
        if (storedPosition) {
          bodyStyle.position = storedPosition;
        } else {
          bodyStyle.removeProperty('position');
        }
        if (storedTop) {
          bodyStyle.top = storedTop;
        } else {
          bodyStyle.removeProperty('top');
        }
        if (storedLeft) {
          bodyStyle.left = storedLeft;
        } else {
          bodyStyle.removeProperty('left');
        }
        if (storedRight) {
          bodyStyle.right = storedRight;
        } else {
          bodyStyle.removeProperty('right');
        }
        if (storedWidth) {
          bodyStyle.width = storedWidth;
        } else {
          bodyStyle.removeProperty('width');
        }
        document.documentElement.style.removeProperty('--scrollbar-gutter');

        if (usedIOSFixedPositionLock) {
          window.scrollTo(0, lockedScrollY);
        }

        storedOverflow = '';
        storedPaddingRight = '';
        storedPosition = '';
        storedTop = '';
        storedLeft = '';
        storedRight = '';
        storedWidth = '';
        storedHtmlOverflow = '';
        storedHtmlOverscrollBehavior = '';
        lockedScrollY = 0;
        usedIOSFixedPositionLock = false;
      }
    };
  }, [enabled]);
}
